import { useState, type FormEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import type { MeDto } from '@bingo/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useLang } from '../lib/i18n';
import { Brand } from '../components/Brand';
import { LangSwitch } from '../components/LangSwitch';
import { PasswordInput } from '../components/PasswordInput';

export function Login() {
  const { me, setMe } = useAuth();
  const { lang, t } = useLang();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [params] = useSearchParams();
  const next = params.get('next') ?? '/';
  // Only our own pages: never a link to another site.
  const target = next.startsWith('/') && !next.startsWith('//') ? next : '/';

  if (me) return <Navigate to={target} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      let who = await api<MeDto>('/api/auth/login', { method: 'POST', body: { email, password } });
      // The language chosen here becomes the person's language.
      if (who.user.language !== lang) who = await api<MeDto>('/api/me/preferences', { method: 'PATCH', body: { language: lang } });
      setMe(who);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Login failed.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <form className="card auth-card" onSubmit={submit}>
        <div className="auth-top">
          <Brand />
          <LangSwitch />
        </div>
        <h1>{t('Log in')}</h1>
        {error && <div className="alert alert-bad" role="alert">{error}</div>}
        <div className="field">
          <label htmlFor="email">{t('Email')}</label>
          <input id="email" className="input" type="email" autoComplete="username" required
            value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="password">{t('Password')}</label>
          <PasswordInput id="password" className="input" autoComplete="current-password" required
            value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-block" disabled={busy}>{busy ? t('Logging in…') : t('Log in')}</button>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          {t('Forgot your password? Ask the direction for a new link.')}
        </p>
      </form>
    </div>
  );
}
